// Setup Checklist Component
// Shows onboarding progress and guides users through setup

import React, { useState, useEffect } from 'react';
import { Check, Circle, ChevronRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SetupChecklist = ({ compact = false }) => {
  const [checklist, setChecklist] = useState({
    branding: false,
    widget_installed: false,
    cms_preference: false,
    notifications_configured: false,
    contact_info: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChecklistStatus();
  }, []);

  const fetchChecklistStatus = async () => {
    try {
      // Auth via httpOnly cookie - no header needed
      const [brandingRes, integrationsRes, cmsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/branding`).catch(() => ({ data: {} })),
        axios.get(`${API}/integrations/status`).catch(() => ({ data: {} })),
        axios.get(`${API}/cms-preference`).catch(() => ({ data: {} })),
        axios.get(`${API}/notifications/settings`).catch(() => ({ data: {} })),
      ]);

      setChecklist({
        branding: !!(brandingRes.data?.primary_color || brandingRes.data?.logo_url),
        widget_installed: localStorage.getItem('widget_copied') === 'true',
        cms_preference: brandingRes.data?.preferred_cms_format && brandingRes.data?.preferred_cms_format !== 'universal',
        notifications_configured: !!(notificationsRes.data?.email_enabled || notificationsRes.data?.sms_enabled),
        contact_info: !!(brandingRes.data?.firm_phone || brandingRes.data?.firm_email),
      });
    } catch {
      // Silent fail - checklist is non-critical
    } finally {
      setLoading(false);
    }
  };

  const checklistItems = [
    {
      key: 'branding',
      title: 'Customize your branding',
      description: 'Add your logo and brand colors',
      link: '/setup',
      linkText: 'Go to Setup Wizard',
    },
    {
      key: 'widget_installed',
      title: 'Install chat widget',
      description: 'Add the widget to your website',
      link: '/embed',
      linkText: 'Get embed code',
    },
    {
      key: 'cms_preference',
      title: 'Set CMS preference',
      description: 'Choose your case management system',
      link: '/setup',
      linkText: 'Select CMS',
    },
    {
      key: 'contact_info',
      title: 'Add firm contact info',
      description: 'Phone & email for client follow-ups',
      link: '/branding',
      linkText: 'Add contact info',
    },
    {
      key: 'notifications_configured',
      title: 'Configure notifications',
      description: 'Set up email and SMS alerts',
      link: '/notifications',
      linkText: 'Configure alerts',
    },
  ];

  const completedCount = Object.values(checklist).filter(Boolean).length;
  const totalCount = checklistItems.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
        <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-10 bg-slate-100 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  // If all completed, show success message
  if (completedCount === totalCount) {
    return (
      <div 
        className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl border border-teal-200 p-4"
        data-tour="setup-checklist"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-teal-900">All Set Up!</h3>
            <p className="text-sm text-teal-700">Your IntakePal is fully configured and ready to capture leads.</p>
          </div>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div 
        className="bg-white rounded-xl border border-slate-200 p-4"
        data-tour="setup-checklist"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">Setup Progress</h3>
          <span className="text-sm font-medium text-teal-600">{progressPercent}%</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2 mb-3">
          <div 
            className="bg-teal-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          ></div>
        </div>
        <p className="text-sm text-slate-600">
          {completedCount} of {totalCount} steps completed
        </p>
        <Link 
          to="/setup" 
          className="mt-3 inline-flex items-center text-sm text-teal-600 hover:text-teal-700 font-medium"
        >
          Continue setup <ChevronRight className="w-4 h-4 ml-1" />
        </Link>
      </div>
    );
  }

  return (
    <div 
      className="bg-white rounded-xl border border-slate-200 p-5"
      data-tour="setup-checklist"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-900">Setup Checklist</h3>
        <span className="text-sm font-medium px-2 py-1 bg-teal-50 text-teal-700 rounded-full">
          {completedCount}/{totalCount} complete
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-slate-100 rounded-full h-2 mb-5">
        <div 
          className="bg-teal-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        ></div>
      </div>

      {/* Checklist items */}
      <div className="space-y-3">
        {checklistItems.map((item) => {
          const isCompleted = checklist[item.key];
          return (
            <div 
              key={item.key}
              className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                isCompleted ? 'bg-teal-50' : 'bg-slate-50 hover:bg-slate-100'
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                isCompleted ? 'bg-teal-500' : 'border-2 border-slate-300'
              }`}>
                {isCompleted ? (
                  <Check className="w-4 h-4 text-white" />
                ) : (
                  <Circle className="w-3 h-3 text-slate-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-medium text-sm ${isCompleted ? 'text-teal-900' : 'text-slate-900'}`}>
                  {item.title}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
              </div>
              {!isCompleted && (
                <Link 
                  to={item.link}
                  className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center whitespace-nowrap"
                >
                  {item.linkText} <ChevronRight className="w-3 h-3 ml-0.5" />
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SetupChecklist;
